import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    output,
    signal,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import {
    DialogComponent,
    InputSearchComponent,
    LiveStatusComponent,
} from '@interacta-shared/ui';
import { NoResultComponent } from '@interacta-shared/ui-common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';
import { NormalizedLinkSection } from '../../models/external-link.model';
import { LinksDialogSectionComponent } from '../links-dialog-section/links-dialog-section.component';

@Component({
    selector: 'interacta-links-dialog',
    templateUrl: './links-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogComponent,
        InputSearchComponent,
        LiveStatusComponent,
        TranslatePipe,
        LinksDialogSectionComponent,
        NoResultComponent,
    ],
})
export class LinksDialogComponent {
    private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
    private readonly translateService = inject(TranslateService);

    readonly normalizedSections = input.required<NormalizedLinkSection[]>();
    readonly isOpen = input.required<boolean>();

    readonly closing = output();
    readonly loadError = output<string>();

    readonly search = new FormControl<string>('');

    private readonly searchValue = toSignal(this.search.valueChanges, {
        initialValue: '',
    });

    readonly filteredSections = computed(() => {
        const q = (this.searchValue() ?? '').toLowerCase().trim();
        if (!q) return this.normalizedSections();
        return this.normalizedSections()
            .map((section) => ({
                ...section,
                groups: section.groups
                    .map((group) => ({
                        ...group,
                        links: group.links.filter((link) =>
                            link.label.toLowerCase().includes(q),
                        ),
                    }))
                    .filter((g) => g.links.length > 0),
            }))
            .filter((s) => s.groups.length > 0);
    });

    readonly totalCount = computed(
        () =>
            this.normalizedSections()
                .flatMap((s) => s.groups)
                .flatMap((g) => g.links).length,
    );

    private readonly searchedQuery = signal<string | null>(null);
    readonly searchResultsStatus = computed(() => {
        if (!this.searchedQuery()) return null;
        const count = this.filteredSections()
            .flatMap((s) => s.groups)
            .flatMap((g) => g.links).length;
        return count
            ? (this.translateService.instant(
                  'SHARED.ACCESSIBILITY.RESULTS_COUNT',
                  { count },
              ) as string)
            : (this.translateService.instant(
                  'DIGITAL_WORKPLACE.NO_LINK',
              ) as string);
    });

    readonly sectionAccordionState = linkedSignal<Record<string, boolean>>(
        () => {
            const temp: Record<string, boolean> = {};
            this.filteredSections().forEach((section) => {
                temp[section.id] = true;
            });
            return temp;
        },
    );

    constructor() {
        this.search.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.searchedQuery.set(null));

        this.search.valueChanges
            .pipe(debounceTime(500), takeUntilDestroyed())
            .subscribe((value) =>
                this.searchedQuery.set(value?.trim() || null),
            );

        toObservable(this.isOpen)
            .pipe(takeUntilDestroyed())
            .subscribe((value) => {
                if (value)
                    this.googleAnalyticsService.sendPageView('Useful Links');
            });
    }

    toggleSection(sectionId: string): void {
        this.sectionAccordionState.update((state) => ({
            ...state,
            [sectionId]: !state[sectionId],
        }));
    }

    closeDialog(): void {
        this.search.reset();
        this.closing.emit();
    }
}
